import {
  decodeInvite,
  decodeInviteToken,
  encodeInviteLink,
  extractInvite,
  sanitizeDisplayName,
} from "@veto/domain";
import { readCompactBundle, toHex, verifyIdentityBundle, type WasmIdentity } from "@veto/crypto-bridge";

export type ParsedInvite = {
  displayName: string;
  messagingPublicKeyHex: string;
  hybridPublicKeyHex: string;
  /** Peer BIP-340 x-only transport key (inbox routing + NIP-42). */
  transportPublicKeyHex?: string;
};

function xOnly(hex: string): string {
  const raw = hex.toLowerCase();
  return raw.length === 66 && (raw.startsWith("02") || raw.startsWith("03")) ? raw.slice(2) : raw;
}

export function keysFromVerifiedBundle(identityBundleJson: string): {
  messagingPublicKeyHex: string;
  hybridPublicKeyHex: string;
  transportPublicKeyHex?: string;
} {
  if (!verifyIdentityBundle(identityBundleJson)) {
    throw new Error("This invite's signature is invalid — ask them to send it again");
  }
  const bundle = JSON.parse(identityBundleJson) as {
    messaging_ed25519_pk: number[];
    hybrid_public: number[];
    transport_secp256k1_pk?: number[];
  };
  if (!Array.isArray(bundle.messaging_ed25519_pk) || !Array.isArray(bundle.hybrid_public)) {
    throw new Error("Invite is missing keys");
  }
  return {
    messagingPublicKeyHex: toHex(new Uint8Array(bundle.messaging_ed25519_pk)),
    hybridPublicKeyHex: toHex(new Uint8Array(bundle.hybrid_public)),
    transportPublicKeyHex: Array.isArray(bundle.transport_secp256k1_pk)
      ? xOnly(toHex(new Uint8Array(bundle.transport_secp256k1_pk)))
      : undefined,
  };
}

/** Map decode failures to short, user-facing reasons (FR-CON-001 fail-closed). */
export function friendlyInviteError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (!raw.trim()) return "That doesn't look like a Veto invite.";
  if (/truncated|damaged|unexpected end|json|base64/i.test(raw)) {
    return "The invite looks cut off or damaged — ask them to send the link again.";
  }
  if (/old version|update veto|unsupported/i.test(raw)) return raw;
  if (/signature/i.test(raw)) return "This invite failed its security check — ask them to send it again.";
  if (/no veto invite/i.test(raw)) return raw;
  return "That doesn't look like a Veto invite — paste the link they sent you.";
}

/**
 * Parse an invite out of ANY text (the whole shared message, a link, a QR
 * payload or a legacy `veto-invite:` string). Every accepted invite is signed:
 * the crypto core verifies the identity bundle before a key is trusted.
 */
export function parseInviteText(raw: string): ParsedInvite {
  const found = extractInvite(raw.trim());
  if (!found) throw new Error("No Veto invite found — paste the link they sent you");
  if (found.kind === "link") {
    const { name, bundle } = decodeInviteToken(found.token);
    const keys = readCompactBundle(bundle);
    if (!keys) throw new Error("This invite's signature is invalid — ask them to send it again");
    return {
      displayName: name || "New contact",
      messagingPublicKeyHex: keys.ed,
      hybridPublicKeyHex: keys.hybrid,
      transportPublicKeyHex: xOnly(keys.transport),
    };
  }
  const inv = decodeInvite(found.text);
  if (inv.v !== 2) {
    // v1 invites were unsigned (no binding between identity and encryption key).
    throw new Error("This invite is from an old version of Veto — ask them to send a new link");
  }
  const keys = keysFromVerifiedBundle(inv.identityBundleJson);
  return { displayName: sanitizeDisplayName(inv.displayName) || "New contact", ...keys };
}

/** Our shareable invite link (payload in the URL fragment — never sent to the server). */
export function buildInviteLink(identity: WasmIdentity, displayName: string, origin = window.location.origin): string {
  return encodeInviteLink({ origin, name: displayName || "Veto user", compactBundle: identity.compactBundle() });
}

/** Text for the OS share sheet / messaging apps. */
export function inviteShareText(displayName: string, link: string): string {
  const who = displayName.trim() || "A friend";
  return `${who} invited you to chat privately on Veto — end-to-end encrypted, no phone number needed.\n\nTap to connect: ${link}`;
}

export function fingerprintPreview(messagingHex: string): string {
  const h = messagingHex.replace(/\s/g, "").toLowerCase();
  if (h.length < 16) return "—";
  return `${h.slice(0, 4)} ${h.slice(4, 8)} … ${h.slice(-8, -4)} ${h.slice(-4)}`;
}

export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}
